import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createBlog, updateBlog, getAdminBlogs, uploadImage, resolveAssetUrl } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { ImageResize } from 'tiptap-extension-resize-image';
import LinkExtension from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { Markdown } from 'tiptap-markdown';

const CATEGORY_KEYS = ['technical', 'news', 'event', 'announcement', 'general'];

const empty = {
  title: '',
  content: '',
  excerpt: '',
  coverImage: '',
  category: 'general',
  status: 'draft',
};

/* ── Toolbar button ──────────────────────────────────────────────────────── */
function ToolBtn({ onClick, active, disabled, title, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '34px',
        height: '34px',
        borderRadius: '7px',
        border: active ? '1.5px solid #f5a623' : '1.5px solid transparent',
        background: active ? '#fff7e6' : 'transparent',
        color: active ? '#d97706' : '#4b5563',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.35 : 1,
        fontSize: '15px',
        transition: 'all 0.12s',
        flexShrink: 0,
      }}
      onMouseEnter={(e) => {
        if (!active && !disabled) {
          e.currentTarget.style.background = '#f9fafb';
          e.currentTarget.style.color = '#111827';
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = '#4b5563';
        }
      }}
    >
      {children}
    </button>
  );
}

function Divider() {
  return (
    <span style={{
      width: '1px',
      height: '24px',
      background: '#e5e7eb',
      margin: '0 6px',
      flexShrink: 0,
      alignSelf: 'center',
    }} />
  );
}

/* ── Link popover ────────────────────────────────────────────────────────── */
function LinkPopover({ onConfirm, onClose, initial = '' }) {
  const [url, setUrl] = useState(initial);
  return (
    <div style={{
      position: 'absolute',
      top: '52px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '10px',
      padding: '12px 14px',
      display: 'flex',
      gap: '8px',
      alignItems: 'center',
      zIndex: 300,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      minWidth: '360px',
    }}>
      <i className="bi bi-link-45deg" style={{ color: '#9ca3af', fontSize: '16px' }} />
      <input
        autoFocus
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') onConfirm(url); if (e.key === 'Escape') onClose(); }}
        placeholder="https://…"
        style={{
          flex: 1,
          border: '1.5px solid #e5e7eb',
          borderRadius: '7px',
          color: '#111827',
          padding: '7px 10px',
          fontSize: '13px',
          outline: 'none',
          background: '#f9fafb',
        }}
      />
      <button type="button" onClick={() => onConfirm(url)} style={{
        background: 'linear-gradient(135deg,#f5a623,#e8920c)',
        border: 'none', borderRadius: '7px', color: 'white',
        padding: '7px 14px', fontWeight: 700, fontSize: '13px', cursor: 'pointer',
      }}>
        OK
      </button>
      <button type="button" onClick={onClose} style={{
        background: '#f3f4f6', border: 'none', borderRadius: '7px',
        color: '#6b7280', padding: '7px 10px', fontSize: '13px', cursor: 'pointer',
      }}>
        <i className="bi bi-x" />
      </button>
    </div>
  );
}

/* ── Toolbar ─────────────────────────────────────────────────────────────── */
function EditorToolbar({ editor, onImageClick, uploading }) {
  const [linkOpen, setLinkOpen] = useState(false);
  if (!editor) return null;

  const confirmLink = (url) => {
    setLinkOpen(false);
    if (!url) { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().setLink({ href: url, target: '_blank' }).run();
  };

  return (
    <div style={{
      background: 'white',
      borderBottom: '1.5px solid #e5e7eb',
      padding: '8px 14px',
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '2px',
      position: 'relative',
      borderRadius: '12px 12px 0 0',
    }}>
      {/* Undo / Redo */}
      <ToolBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo (Ctrl+Z)">
        <i className="bi bi-arrow-counterclockwise" />
      </ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo (Ctrl+Y)">
        <i className="bi bi-arrow-clockwise" />
      </ToolBtn>

      <Divider />

      {/* Text style */}
      <ToolBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold (Ctrl+B)">
        <i className="bi bi-type-bold" />
      </ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic (Ctrl+I)">
        <i className="bi bi-type-italic" />
      </ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline (Ctrl+U)">
        <i className="bi bi-type-underline" />
      </ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough">
        <i className="bi bi-type-strikethrough" />
      </ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Inline Code">
        <i className="bi bi-code" />
      </ToolBtn>

      <Divider />

      {/* Headings */}
      {[1, 2, 3].map((level) => (
        <ToolBtn
          key={level}
          onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
          active={editor.isActive('heading', { level })}
          title={`Heading ${level}`}
        >
          <span style={{ fontSize: '11px', fontWeight: 900, letterSpacing: '-0.5px' }}>H{level}</span>
        </ToolBtn>
      ))}

      <Divider />

      {/* Lists */}
      <ToolBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet List">
        <i className="bi bi-list-ul" />
      </ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Ordered List">
        <i className="bi bi-list-ol" />
      </ToolBtn>

      <Divider />

      {/* Blocks */}
      <ToolBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Blockquote">
        <i className="bi bi-quote" />
      </ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title="Code Block">
        <i className="bi bi-code-square" />
      </ToolBtn>

      <Divider />

      {/* Align */}
      <ToolBtn onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align Left">
        <i className="bi bi-text-left" />
      </ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align Center">
        <i className="bi bi-text-center" />
      </ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align Right">
        <i className="bi bi-text-right" />
      </ToolBtn>

      <Divider />

      {/* Link */}
      <ToolBtn onClick={() => setLinkOpen((v) => !v)} active={editor.isActive('link') || linkOpen} title="Insert / Edit Link">
        <i className="bi bi-link-45deg" />
      </ToolBtn>
      {editor.isActive('link') && (
        <ToolBtn onClick={() => editor.chain().focus().unsetLink().run()} title="Remove Link">
          <i className="bi bi-link-break" style={{ color: '#ef4444' }} />
        </ToolBtn>
      )}

      {/* Image upload */}
      <ToolBtn onClick={onImageClick} disabled={uploading} title="Upload Image">
        {uploading
          ? <span className="spinner-border spinner-border-sm" style={{ width: '13px', height: '13px', borderWidth: '2px', color: '#9ca3af' }} />
          : <i className="bi bi-image" />}
      </ToolBtn>

      <Divider />

      {/* HR + clear */}
      <ToolBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal Rule">
        <i className="bi bi-hr" />
      </ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} title="Clear Formatting">
        <i className="bi bi-eraser" />
      </ToolBtn>

      {linkOpen && (
        <LinkPopover
          initial={editor.getAttributes('link').href || ''}
          onConfirm={confirmLink}
          onClose={() => setLinkOpen(false)}
        />
      )}
    </div>
  );
}

/* ── Main component ──────────────────────────────────────────────────────── */
export default function AdminPostForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEdit);
  const [error, setError] = useState('');
  const contentInitialized = useRef(false);
  const imageInputRef = useRef(null);
  const coverInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      LinkExtension.configure({ openOnClick: false, autolink: true }),
      ImageResize,
      Placeholder.configure({ placeholder: t('Home.Admin.ContentPlaceholder', 'Write your post content here…') }),
      Markdown.configure({ html: true, tightLists: true, transformPastedText: true }),
    ],
    onUpdate: ({ editor }) => {
      const md = editor.storage.markdown.getMarkdown();
      setForm((f) => ({ ...f, content: md }));
    },
  });

  useEffect(() => {
    if (!isEdit) return;
    getAdminBlogs()
      .then((res) => {
        const blog = res.data.find((b) => b.id === parseInt(id));
        if (!blog) { navigate('/admin'); return; }
        setForm({
          title: blog.title,
          content: blog.content,
          excerpt: blog.excerpt || '',
          coverImage: blog.coverImage || '',
          category: blog.category,
          status: blog.status,
        });
      })
      .catch(() => navigate('/admin'))
      .finally(() => setFetchLoading(false));
  }, [id, isEdit, navigate]);

  useEffect(() => {
    if (editor && form.content && !contentInitialized.current) {
      editor.commands.setContent(form.content);
      contentInitialized.current = true;
    }
  }, [editor, form.content]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleImageFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await uploadImage(fd);
      editor.chain().focus().setImage({ src: res.data.url, alt: file.name }).run();
    } catch {
      setError('Image upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleCoverFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await uploadImage(fd);
      setForm((f) => ({ ...f, coverImage: res.data.url }));
    } catch {
      setError('Cover image upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const savePost = async (overrideStatus) => {
    if (!form.title.trim()) { setError(t('Home.Admin.TitleRequired', 'Title is required.')); return; }
    if (!form.content.trim()) { setError(t('Home.Admin.ContentRequired', 'Content is required.')); return; }
    setError('');
    setLoading(true);
    try {
      const payload = {
        ...form,
        status: overrideStatus ?? form.status,
        excerpt: form.excerpt || undefined,
        coverImage: form.coverImage || undefined,
      };
      if (isEdit) await updateBlog(parseInt(id), payload);
      else await createBlog(payload);
      navigate('/admin');
    } catch (err) {
      const msg = err.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join(', ') : msg || 'Failed to save post');
    } finally {
      setLoading(false);
    }
  };

  const categoryLabel = (key) => ({
    technical: t('Home.Blog.Technical'),
    news: t('Home.Blog.News'),
    event: t('Home.Blog.Event'),
    announcement: t('Home.Blog.Announcement'),
    general: t('Home.Blog.General'),
  }[key] || key);

  const sidebarPanelStyle = {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 8px rgba(0,0,0,0.07)',
    overflow: 'hidden',
    border: '1px solid #f0f0f0',
  };

  const panelHeaderStyle = {
    padding: '13px 18px',
    borderBottom: '1px solid #f3f4f6',
    fontWeight: 700,
    fontSize: '11px',
    color: '#6b7280',
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    background: '#fafafa',
  };

  if (fetchLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f9fc' }}>
        <div className="spinner-border text-warning" role="status" />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fc', direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* Hidden file inputs */}
      <input ref={imageInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageFile} />
      <input ref={coverInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleCoverFile} />

      <div style={{ display: 'flex', minHeight: '100vh' }}>

        {/* Sidebar nav */}
        <div style={{
          width: '220px',
          background: 'linear-gradient(180deg, #1a1a2e 0%, #0f3460 100%)',
          color: 'white',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          height: '100vh',
          order: isRTL ? 1 : 0,
        }}>
          <div style={{ padding: '28px 24px 20px' }}>
            <img src="/img/logo.png" alt="ENGPI" height="45" />
            <div style={{ fontSize: '11px', opacity: 0.45, marginTop: '8px', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
              {t('Home.Admin.PanelTitle')}
            </div>
          </div>
          <nav style={{ flex: 1, padding: '8px 12px' }}>
            <Link
              to="/admin"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 12px',
                borderRadius: '8px',
                color: 'rgba(255,255,255,0.65)',
                textDecoration: 'none',
                fontSize: '14px',
                flexDirection: isRTL ? 'row-reverse' : 'row',
              }}
            >
              <i className={isRTL ? 'bi bi-arrow-right' : 'bi bi-arrow-left'} />
              {t('Home.Admin.BackToDashboard')}
            </Link>
          </nav>
          <div style={{ padding: '16px 24px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ fontSize: '13px', opacity: 0.7, marginBottom: '12px' }}>{user?.fullName}</div>
            <button
              onClick={() => { logout(); navigate('/login'); }}
              style={{
                background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)',
                color: 'white', borderRadius: '6px', padding: '6px 12px',
                fontSize: '12px', cursor: 'pointer', width: '100%',
              }}
            >
              <i className="bi bi-box-arrow-left me-1" /> {t('Home.Admin.Logout')}
            </button>
          </div>
        </div>

        {/* Main */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

          {/* Sticky top bar */}
          <div style={{
            background: 'white',
            borderBottom: '1px solid #e5e7eb',
            padding: '14px 36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 200,
            flexDirection: isRTL ? 'row-reverse' : 'row',
          }}>
            <div>
              <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '3px' }}>
                <Link to="/admin" style={{ color: '#9ca3af', textDecoration: 'none' }}>
                  {t('Home.Admin.PanelTitle')}
                </Link>
                {' / '}
                <span style={{ color: '#374151', fontWeight: 600 }}>
                  {isEdit ? t('Home.Admin.EditPost') : t('Home.Admin.NewPostTitle')}
                </span>
              </div>
              <h5 style={{ margin: 0, fontWeight: 800, color: '#111827', fontSize: '17px', letterSpacing: '-0.01em' }}>
                {isEdit ? t('Home.Admin.EditPost') : t('Home.Admin.NewPostTitle')}
              </h5>
            </div>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <Link to="/admin" style={{
                padding: '8px 16px', borderRadius: '8px', border: '1px solid #d1d5db',
                color: '#374151', textDecoration: 'none', fontSize: '13px', fontWeight: 500,
                background: 'white', lineHeight: 1, display: 'inline-flex', alignItems: 'center',
              }}>
                {t('Home.Admin.Cancel')}
              </Link>
              <button type="button" onClick={() => savePost('draft')} disabled={loading} style={{
                padding: '8px 16px', borderRadius: '8px', border: '1px solid #d1d5db',
                color: '#374151', fontSize: '13px', fontWeight: 500, background: 'white',
                cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
                display: 'inline-flex', alignItems: 'center', gap: '5px',
              }}>
                <i className="bi bi-floppy" />
                {t('Home.Admin.SaveDraft', 'Save Draft')}
              </button>
              <button type="button" onClick={() => savePost('published')} disabled={loading} style={{
                padding: '8px 20px', borderRadius: '8px',
                background: 'linear-gradient(135deg, #f5a623 0%, #e8920c 100%)',
                color: 'white', border: 'none', fontSize: '13px', fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.75 : 1,
                boxShadow: '0 2px 10px rgba(245,166,35,0.4)',
                display: 'inline-flex', alignItems: 'center', gap: '6px',
              }}>
                {loading ? (
                  <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />{t('Home.Admin.Saving')}</>
                ) : (
                  <><i className="bi bi-send-fill" />{isEdit ? t('Home.Admin.UpdatePost') : t('Home.Admin.Publish', 'Publish')}</>
                )}
              </button>
            </div>
          </div>

          {error && (
            <div style={{ padding: '12px 36px 0' }}>
              <div className="alert alert-danger py-2 mb-0 d-flex align-items-center gap-2" style={{ fontSize: '14px' }}>
                <i className="bi bi-exclamation-circle-fill" />{error}
              </div>
            </div>
          )}

          {/* Editor area */}
          <div style={{ flex: 1, padding: '28px 36px 40px', overflowY: 'auto' }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

              {/* Title */}
              <div style={{ marginBottom: '24px' }}>
                <input
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  placeholder={t('Home.Admin.PostTitlePlaceholder', 'Post title…')}
                  style={{
                    width: '100%', border: 'none',
                    borderBottom: '2px solid #e5e7eb',
                    padding: '6px 2px', fontSize: '30px', fontWeight: 800,
                    color: '#111827', outline: 'none', background: 'transparent',
                    textAlign: isRTL ? 'right' : 'left',
                    letterSpacing: '-0.02em', transition: 'border-color 0.2s',
                  }}
                  onFocus={(e) => (e.target.style.borderBottomColor = '#f5a623')}
                  onBlur={(e) => (e.target.style.borderBottomColor = '#e5e7eb')}
                />
              </div>

              {/* Two-column: editor + settings */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isRTL ? '300px 1fr' : '1fr 300px',
                gap: '20px',
                alignItems: 'start',
              }}>

                {/* TipTap editor */}
                <div style={{ order: isRTL ? 1 : 0 }}>
                  <div style={{
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 16px rgba(0,0,0,0.07)',
                    border: '1px solid #e5e7eb',
                  }}>
                    <EditorToolbar
                      editor={editor}
                      onImageClick={() => imageInputRef.current?.click()}
                      uploading={uploading}
                    />
                    <EditorContent editor={editor} className="tiptap-editor-content" />
                  </div>
                  {!form.content && (
                    <p style={{ fontSize: '12px', color: '#ef4444', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <i className="bi bi-exclamation-circle" />
                      {t('Home.Admin.ContentRequired', 'Content is required')}
                    </p>
                  )}
                </div>

                {/* Settings sidebar */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', order: isRTL ? 0 : 1 }}>

                  <div style={sidebarPanelStyle}>
                    <div style={panelHeaderStyle}>
                      <i className="bi bi-sliders" />
                      {t('Home.Admin.PostDetails')}
                    </div>
                    <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div>
                        <label style={{ fontSize: '11px', fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                          {t('Home.Admin.StatusLabel')}
                        </label>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          {['draft', 'published'].map((s) => (
                            <button key={s} type="button" onClick={() => setForm((f) => ({ ...f, status: s }))} style={{
                              flex: 1, padding: '8px 0', borderRadius: '8px',
                              border: form.status === s ? 'none' : '1px solid #e5e7eb',
                              background: form.status === s
                                ? (s === 'published' ? 'linear-gradient(135deg,#10b981,#059669)' : 'linear-gradient(135deg,#374151,#1f2937)')
                                : 'white',
                              color: form.status === s ? 'white' : '#9ca3af',
                              fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                              transition: 'all 0.15s',
                              boxShadow: form.status === s ? '0 2px 6px rgba(0,0,0,0.15)' : 'none',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                            }}>
                              <i className={s === 'published' ? 'bi bi-globe2' : 'bi bi-pencil'} />
                              {s === 'draft' ? t('Home.Admin.Draft') : t('Home.Admin.Published')}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label style={{ fontSize: '11px', fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                          {t('Home.Admin.CategoryLabel')}
                        </label>
                        <select name="category" value={form.category} onChange={handleChange} style={{
                          width: '100%', padding: '9px 12px', borderRadius: '8px',
                          border: '1px solid #e5e7eb', fontSize: '13px', color: '#374151',
                          background: 'white', outline: 'none', cursor: 'pointer', fontWeight: 500,
                        }}>
                          {CATEGORY_KEYS.map((c) => <option key={c} value={c}>{categoryLabel(c)}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div style={sidebarPanelStyle}>
                    <div style={panelHeaderStyle}>
                      <i className="bi bi-blockquote-left" />
                      {t('Home.Admin.ExcerptLabel')}
                      <span style={{ marginInlineStart: 'auto', fontWeight: 400, fontSize: '10px', color: '#d1d5db', textTransform: 'none', letterSpacing: 0 }}>
                        {t('Home.Admin.ExcerptOptional')}
                      </span>
                    </div>
                    <div style={{ padding: '14px 18px' }}>
                      <textarea
                        name="excerpt" value={form.excerpt} onChange={handleChange}
                        placeholder={t('Home.Admin.ExcerptPlaceholder')} rows={4}
                        style={{
                          width: '100%', padding: '10px 12px', borderRadius: '8px',
                          border: '1px solid #e5e7eb', fontSize: '13px', color: '#374151',
                          resize: 'vertical', outline: 'none', fontFamily: 'inherit',
                          lineHeight: '1.65', textAlign: isRTL ? 'right' : 'left', background: '#fafafa',
                          transition: 'border-color 0.15s, background 0.15s',
                        }}
                        onFocus={(e) => { e.target.style.borderColor = '#f5a623'; e.target.style.background = 'white'; }}
                        onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; e.target.style.background = '#fafafa'; }}
                      />
                    </div>
                  </div>

                  <div style={sidebarPanelStyle}>
                    <div style={panelHeaderStyle}>
                      <i className="bi bi-image" />
                      {t('Home.Admin.CoverImageLabel')}
                      <span style={{ marginInlineStart: 'auto', fontWeight: 400, fontSize: '10px', color: '#d1d5db', textTransform: 'none', letterSpacing: 0 }}>
                        {t('Home.Admin.ExcerptOptional')}
                      </span>
                    </div>
                    <div style={{ padding: '14px 18px' }}>
                      {/* Preview */}
                      {form.coverImage && (
                        <div style={{ position: 'relative', marginBottom: '12px', borderRadius: '8px', overflow: 'hidden' }}>
                          <img
                            src={resolveAssetUrl(form.coverImage)}
                            alt="cover preview"
                            style={{ width: '100%', height: '130px', objectFit: 'cover', display: 'block' }}
                            onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                          />
                          <button
                            type="button"
                            onClick={() => setForm((f) => ({ ...f, coverImage: '' }))}
                            style={{
                              position: 'absolute', top: '6px', right: '6px',
                              background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none',
                              borderRadius: '50%', width: '26px', height: '26px',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              cursor: 'pointer', fontSize: '14px',
                            }}
                          >
                            <i className="bi bi-x" />
                          </button>
                        </div>
                      )}

                      {/* Upload button */}
                      <button
                        type="button"
                        onClick={() => coverInputRef.current?.click()}
                        disabled={uploading}
                        style={{
                          width: '100%',
                          padding: '9px 12px',
                          borderRadius: '8px',
                          border: '2px dashed #d1d5db',
                          background: '#fafafa',
                          color: '#6b7280',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: uploading ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '7px',
                          marginBottom: '10px',
                          transition: 'border-color 0.15s, color 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#f5a623'; e.currentTarget.style.color = '#d97706'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#6b7280'; }}
                      >
                        {uploading
                          ? <><span className="spinner-border spinner-border-sm" style={{ width: '13px', height: '13px', borderWidth: '2px' }} /> Uploading…</>
                          : <><i className="bi bi-cloud-upload" /> Upload Image</>
                        }
                      </button>

                      {/* OR URL input */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <hr style={{ flex: 1, margin: 0, borderColor: '#e5e7eb' }} />
                        <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 600 }}>OR</span>
                        <hr style={{ flex: 1, margin: 0, borderColor: '#e5e7eb' }} />
                      </div>

                      <input
                        type="url"
                        name="coverImage"
                        value={form.coverImage}
                        onChange={handleChange}
                        placeholder="https://…"
                        style={{
                          width: '100%', padding: '9px 12px', borderRadius: '8px',
                          border: '1px solid #e5e7eb', fontSize: '13px', color: '#374151',
                          outline: 'none', background: '#fafafa',
                          transition: 'border-color 0.15s, background 0.15s',
                        }}
                        onFocus={(e) => { e.target.style.borderColor = '#f5a623'; e.target.style.background = 'white'; }}
                        onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; e.target.style.background = '#fafafa'; }}
                      />
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
